import React, { useState } from "react";
import { useAppContext } from "../../context/appContext";
import { FaTimes } from "react-icons/fa";
import FileUpload from "./PhotoModal/FileUpload";
import AddMethodButton from "./PhotoModal/AddMethodButton";
import { iterateName } from "../../utils/fileNames";
import { normalizeImage, previewImage } from "../../utils/normalizeImage";

function PhotoModal({ name, setShowPhotoModal, beforeAfter }) {
  const {
    job,
    files,
    photos,
    handlePrimativeChange,
    createRecord,
    uploadFile,
    getDimensions,
    isLoading,
  } = useAppContext();

  const addMethods = ["folder", "upload", "camera"];
  const [addMethod, setAddMethod] = useState(addMethods[0]);
  const [selectedPhotos, setSelecetPhotos] = useState([]);
  const [fileItems, setFileItems] = useState([]);
  const [reduceSize, setReduceSize] = useState(true);
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const imageTypes = ["jpg", "jpeg", "png", "gif", "bmp", "svg"];

  const getPhotos = () => {
    let photosOnly = [];
    let uniquePhotos = [];

    if (files.length > 0) {
      photosOnly = files?.filter((file) =>
        imageTypes.some((ext) => file.name.toLowerCase().includes(ext))
      );
    }
    if (photosOnly.length > 0) {
      uniquePhotos = photosOnly.filter((photo) => {
        return !photos?.some((p) => {
          return p.photo_name === photo.name;
        });
      });
    }
    return uniquePhotos;
  };

  const cleanup = () => {
    setSelecetPhotos([]);
    setFileItems([]);
    setAddMethod(addMethods[0]);
    setReduceSize(true);
    setUploadCount(0);
    setUploadTotal(0);
    setShowPhotoModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isBefore = beforeAfter === "Before";
    let newPhotoNames = [];
    const uniquePhotos = getPhotos();
    // Method 1 - Add files from job folder
    if (addMethod === addMethods[0]) {
      newPhotoNames = selectedPhotos.map((i) => uniquePhotos[i].name);
    }
    // Method 2 - Upload new photos & Method 3 - Take photo with camera
    if (addMethod === addMethods[1] || addMethod === addMethods[2]) {
      const jobPath = job.folder_path;
      for (let i = 0; i < fileItems.length; i++) {
        const item = fileItems[i];
        setUploadCount(i + 1);
        setUploadTotal(fileItems.length);
        try {
          const normalizedFile = await normalizeImage(
            item.fileMetadata,
            item.newName,
            { reduce: reduceSize }
          );
          const uploadedFile = await uploadFile({
            file: normalizedFile,
            jobPath,
          });
          if (uploadedFile) {
            newPhotoNames.push(normalizedFile.name);
          }
        } catch (error) {
          console.error("Error uploading photo:", item.fileMetadata.name, error);
        }
      }
      setUploadTotal(0);
    }
    // Build out the full photo object for each of the names
    const newPhotos = await Promise.all(
      newPhotoNames.map(async (fileName) => {
        try {
          const photo_name = fileName.replace(".heic", ".jpg");
          const job_id = job.id;
          const category = isBefore ? "Before Photos" : "After Photos";
          const filePath = `${job.folder_path}/${photo_name}`;
          const { width, height } = await getDimensions(filePath);

          let newPhoto = { photo_name, job_id, category, width, height };

          // Add record to database
          newPhoto = await createRecord(
            `/photos/create`,
            newPhoto,
            "before/after photo"
          );
          return newPhoto;
        } catch (error) {
          console.error("Error adding photo:", fileName, error);
          // Return null to indicate failure and allow the map function to continue
          return null;
        }
      })
    );
    // Filter out any null values if any
    const successfulPhotos = newPhotos.filter((photo) => photo !== null);
    // Update state with new selected photos
    const addPhotos = [...photos, ...successfulPhotos];
    handlePrimativeChange({
      property: "photos",
      value: addPhotos,
    });
    // Clean up
    cleanup();
  };

  const handlePhotoClick = (index) => {
    let newSelections = [];
    if (selectedPhotos.includes(index)) {
      newSelections = selectedPhotos.filter((i) => i !== index);
    } else {
      newSelections = [...selectedPhotos, index];
    }
    setSelecetPhotos(newSelections);
  };

  const handleFileInput = async (e) => {
    const fileList = Array.from(e.target.files);
    const baseFileName = `${job.job_number} ${beforeAfter} Photo`;
    const newItems = [];
    const assignedNames = [];
    for (const file of fileList) {
      let imagePreview = null;
      try {
        imagePreview = await previewImage(file);
      } catch (error) {
        console.error("Error generating preview:", file.name, error);
      }
      const takenNames = [...files, ...assignedNames.map((n) => ({ name: n }))];
      const itemName = iterateName(baseFileName, takenNames);
      assignedNames.push(itemName);
      newItems.push({
        fileMetadata: file,
        newName: itemName,
        imagePreview,
      });
    }
    setFileItems(newItems);
  };

  const handleNewName = (value, index) => {
    const updatedItems = [...fileItems];
    if (updatedItems.length === 1) {
      updatedItems[0] = { ...updatedItems[0], newName: value };
    } else if (Number.isInteger(index)) {
      updatedItems[index] = { ...updatedItems[index], newName: value };
    }
    setFileItems(updatedItems);
  };

  const handleCancel = (e) => {
    console.log("action cancelled...");
    cleanup();
  };

  const uploadFileProps = {
    fileItems,
    setNewName: handleNewName,
    reduceSize,
    setReduceSize,
    uploadCount,
    uploadTotal,
  };

  return (
    <form className="form photo-modal">
      {/* Close Button */}
      <button className="close-btn" type="button" onClick={handleCancel}>
        <FaTimes />
      </button>
      {/* Prompt Message */}
      <div className="form-center sub-item confirm-report">
        <h3>{`Add new ${beforeAfter} photo`}</h3>
        <div className="btn-container flex-parent whole">
          {/* Add from Job Folder Button - Method 1 */}
          <AddMethodButton
            addMethod={addMethod}
            method={addMethods[0]}
            buttonLabel="Job Folder"
            setAddMethod={setAddMethod}
            inputType="button"
          />
          {/* Upload Files Button - Method 2 */}
          <AddMethodButton
            accept="image/*"
            addMethod={addMethod}
            method={addMethods[1]}
            buttonLabel="Upload Files"
            handleFileInput={handleFileInput}
            inputType="file"
            multiple={true}
            setAddMethod={setAddMethod}
          />
          {/* Use Camera - Method 3 */}
          <AddMethodButton
            addMethod={addMethod}
            method={addMethods[2]}
            buttonLabel="Use Camera"
            capture={true}
            handleFileInput={handleFileInput}
            inputType="file"
            setAddMethod={setAddMethod}
          />
        </div>
        {/* Method 1 - Add from Job Folder - List of Files Section */}
        {addMethod === addMethods[0] && (
          <div className="flex-parent whole">
            {getPhotos().map((file, i) => {
              const thumbnail = file.thumbnails[0]?.medium;
              return (
                <img
                  src={thumbnail?.url}
                  key={i}
                  className={
                    selectedPhotos.includes(i)
                      ? "selected-photo thumbnail"
                      : "thumbnail"
                  }
                  height={thumbnail.height}
                  width={thumbnail.width}
                  alt={file.name}
                  onClick={() => handlePhotoClick(i)}
                />
              );
            })}
          </div>
        )}
        {/* Method 2/3 - Uploaded Files - File Info Section */}
        {(addMethod === addMethods[1] || addMethod === addMethods[2]) && (
          <FileUpload {...uploadFileProps} />
        )}
      </div>

      {/* Submit/ Clear Buttons */}
      <div className="btn-container flex-parent whole">
        <div className="one-half">
          <button
            name={name}
            className="btn btn-block"
            type="button"
            disabled={isLoading}
            onClick={handleSubmit}
          >
            {isLoading ? "please wait..." : "confirm"}
          </button>
        </div>
        <div className="one-half">
          <button
            className="btn btn-block red"
            type="button"
            disabled={isLoading}
            onClick={handleCancel}
          >
            cancel
          </button>
        </div>
      </div>
    </form>
  );
}
export default PhotoModal;
